package xyz.tyiu.satsprice

/**
 * ISO 3166-1 alpha-2 codes of every country that issues each ISO 4217 currency code shared by
 * multiple countries with no single issuer, so all of their flags can be shown side by side.
 */
private val MULTI_COUNTRY_CURRENCY_REGION_CODES: Map<String, List<String>> = mapOf(
    "XAF" to listOf("CF", "CG", "CM", "GA", "GQ", "TD"),
    "XCD" to listOf("AG", "AI", "DM", "GD", "KN", "LC", "MS", "VC"),
    "XCG" to listOf("CW", "SX"),
    "XOF" to listOf("BF", "BJ", "CI", "GW", "ML", "NE", "SN", "TG"),
    "XPF" to listOf("NC", "PF", "WF"),
)

/** Above this many issuing countries, showing every flag side by side is too visually noisy. */
private const val MAX_FLAGS_PER_CURRENCY = 3

/**
 * ISO 3166-1 alpha-2 codes of every country that issues [code], derived from the first two
 * letters of the ISO 4217 code — which double as the issuing country's ISO 3166-1 alpha-2 code
 * for ordinary national currencies — or empty when [code] isn't tied to any country.
 * [MULTI_COUNTRY_CURRENCY_REGION_CODES] lists every country sharing a currency with no single
 * issuer; the remaining "X"-prefixed codes are precious metals, testing codes, and other
 * non-national codes (XAU, XTS, XXX, ...), none of which has a country. EUR is a special case,
 * using the EU's own region code. Shared by [currencyFlagEmoji] and currency search-by-country.
 */
internal fun issuingCountryCodes(code: String): List<String> {
    MULTI_COUNTRY_CURRENCY_REGION_CODES[code]?.let { return it }
    if (code.startsWith("X")) return emptyList()
    val regionCode = if (code == "EUR") "EU" else code.take(2)
    if (regionCode.length != 2 || regionCode.any { it !in 'A'..'Z' }) return emptyList()
    return listOf(regionCode)
}

/**
 * A country flag emoji for [code], or null when there's no flag to show — either because [code]
 * isn't tied to any country, because it's shared by more than [MAX_FLAGS_PER_CURRENCY] countries
 * (too visually noisy to show side by side), or because [supportsFlagEmoji] says the platform
 * can't render one anyway. [supportsFlagEmoji] defaults to the expect/actual of the same name,
 * which is false for Compose Multiplatform's canvas-based Skia renderer on web (no bundled or
 * system color-emoji font to fall back to, so a flag's regional-indicator codepoints draw as
 * empty boxes there) - callers that render through a real DOM text node instead (e.g. Compose
 * HTML) can pass `true` explicitly, since the browser's own font stack handles it fine.
 */
fun currencyFlagEmoji(code: String, supportsFlagEmoji: Boolean = supportsFlagEmoji()): String? {
    if (!supportsFlagEmoji) return null
    val regionCodes = issuingCountryCodes(code)
    if (regionCodes.isEmpty() || regionCodes.size > MAX_FLAGS_PER_CURRENCY) return null
    return regionCodes.joinToString(" ") { regionFlagEmoji(it) }
}

/** The flag emoji for the 2-letter ISO 3166-1 alpha-2 [regionCode]. */
private fun regionFlagEmoji(regionCode: String): String =
    regionCode.map { regionalIndicatorSymbol(it) }.joinToString("")

/** The Unicode Regional Indicator Symbol for [letter] ('A'..'Z'), as a surrogate pair. */
private fun regionalIndicatorSymbol(letter: Char): String {
    val codePoint = 0x1F1E6 + (letter - 'A')
    val offset = codePoint - 0x10000
    val high = 0xD800 + (offset shr 10)
    val low = 0xDC00 + (offset and 0x3FF)
    return "${high.toChar()}${low.toChar()}"
}
